import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import IntroSequence from '../components/Intro/IntroSequence';
import SplitScreen from '../components/Home/SplitScreen';
import AphorismChat from '../components/Home/AphorismChat';
import HomeCTABlock from '../components/Home/HomeCTABlock';
import Footer from '../components/Footer/Footer';
import { useLang } from '../lib/lang';
import { initIdlePrefetch } from '../lib/sectionPrefetch';

const SESSION_KEY = 'araia_intro_shown';

// iter69·c — Meta description FORZATA SEMPRE in inglese (cliente
// request). WhatsApp / Facebook / LinkedIn / Twitter mostreranno la
// stessa versione EN a prescindere dalla lingua UI dell'utente. Il
// title può restare bilingue perché la UI switcha lingua, ma la
// description social è unificata per coerenza di brand internazionale.
const HOME_DESCRIPTION_EN =
    'ARAIA ALL DESIGN unites Cinzia Araia (Creative Director & Footwear Designer) and Paolo Tommasin (Footwear Design & Product Development). Luxury footwear collections, creative direction and product development. Designed in Italy · Strà, Venice.';

const HOME_META = {
    it: {
        title: 'ARAIA ALL DESIGN — Two Vision, One Creative Direction',
        description: HOME_DESCRIPTION_EN,
    },
    en: {
        title: 'ARAIA ALL DESIGN — Two Visions, One Creative Direction',
        description: HOME_DESCRIPTION_EN,
    },
};

/**
 * Home page. Runs the cinematic intro on first visit of the session,
 * then reveals the split hero with the two side-menus and a shared
 * contacts footer below (accessible by scrolling under the hero).
 */
export default function Home() {
    const lang = useLang();
    const meta = HOME_META[lang] || HOME_META.it;
    const [showIntro, setShowIntro] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            return sessionStorage.getItem(SESSION_KEY) !== '1';
        } catch (e) {
            return true;
        }
    });

    useEffect(() => {
        if (showIntro) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
        return () => document.body.classList.remove('no-scroll');
    }, [showIntro]);

    useEffect(() => {
        document.title = meta.title;
        // Ensure home always opens at the top — no auto-scroll into
        // the "Two Visions Ask" section triggered by focus/mount side-effects.
        try { window.scrollTo(0, 0); }
        catch (e) { console.debug('[Home] scrollTo skipped:', e); }
    }, [meta.title]);

    // iter90 · Strategia C — Idle prefetch orchestrator.
    // Boots ONLY once the intro is done (so it never contends with the
    // LCP-critical hero paint). Chains chunk fetch + HOT slugs data
    // (Bio, My Vision, My Journey) inside requestIdleCallback with LCP
    // gating. Respects Save-Data + 2g/slow-2g via shouldPrefetch().
    // See lib/sectionPrefetch.js for full flow.
    useEffect(() => {
        if (showIntro) return; // wait for intro to complete
        const cleanup = initIdlePrefetch(lang);
        return cleanup;
    }, [showIntro, lang]);

    const handleIntroComplete = () => {
        try {
            sessionStorage.setItem(SESSION_KEY, '1');
        } catch (e) {
            console.debug('[Home] optional operation failed:', e);
        }
        setShowIntro(false);
        // iter74 · Notify listeners (e.g. CookieBanner) that the cinematic
        // intro has finished so they can slide in without competing with it.
        try {
            window.dispatchEvent(new CustomEvent('araia:intro-complete'));
        } catch (e) {
            console.debug('[Home] intro-complete dispatch failed:', e);
        }
    };

    return (
        <div className="araia-root" data-testid="araia-root" lang={lang}>
            <Helmet>
                <html lang={lang} />
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                {/* iter69 · OG + Twitter completo — was missing og:url, og:image,
                    og:locale + canonical. */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:site_name" content="ARAIA ALL DESIGN" />
                <meta property="og:url" content={`https://araialldesign.com${lang === 'en' ? '/en' : '/'}`} />
                <meta property="og:image" content="https://araialldesign.com/og-image.png" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:type" content="image/png" />
                <meta property="og:image:alt" content="ARAIA ALL DESIGN — Two Vision, One Creative Direction" />
                {/* iter69·c — locale forzato en_US, no alternate. */}
                <meta property="og:locale" content="en_US" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={meta.title} />
                <meta name="twitter:description" content={meta.description} />
                <meta name="twitter:image" content="https://araialldesign.com/og-image.png" />
                <link
                    rel="canonical"
                    href={`https://araialldesign.com${lang === 'en' ? '/en' : '/'}`}
                />
                <link
                    rel="alternate"
                    hrefLang="it"
                    href="https://araialldesign.com/"
                />
                <link
                    rel="alternate"
                    hrefLang="en"
                    href="https://araialldesign.com/en"
                />
                <link
                    rel="alternate"
                    hrefLang="x-default"
                    href="https://araialldesign.com/"
                />
                {/* iter69 · Enhanced Organization + WebSite JSON-LD.
                    Address updated to the new Via Chiesa 61 studio, plus
                    email, contactPoint, sameAs stubs and 2 Person
                    founders with jobTitle for GEO citation-quality. */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@graph': [
                            {
                                '@type': 'Organization',
                                '@id': 'https://araialldesign.com/#organization',
                                name: 'ARAIA ALL DESIGN',
                                alternateName: 'ARAIA ALL DESIGN Studio',
                                url: 'https://araialldesign.com',
                                logo: 'https://araialldesign.com/assets/logos/logo.png',
                                email: 'info@alldesignsrl.com',
                                // iter69·c — description ALWAYS in English for
                                // consistency across social scrapers and GEO.
                                description: HOME_DESCRIPTION_EN,
                                founder: [
                                    {
                                        '@type': 'Person',
                                        '@id': 'https://araialldesign.com/#cinzia',
                                        name: 'Cinzia Araia',
                                        jobTitle: 'Creative Director — Footwear Designer',
                                        // iter69·b — birthPlace refined from generic
                                        // "Riviera del Brenta" to the city-level entity
                                        // Strà (Venezia), which has stronger
                                        // international recognisability and matches
                                        // Organization.address for citation-quality.
                                        birthPlace: {
                                            '@type': 'Place',
                                            name: 'Strà, Venezia, Italy',
                                            address: {
                                                '@type': 'PostalAddress',
                                                addressLocality: 'Strà',
                                                addressRegion: 'Venezia',
                                                addressCountry: 'IT',
                                            },
                                        },
                                        worksFor: { '@id': 'https://araialldesign.com/#organization' },
                                    },
                                    {
                                        '@type': 'Person',
                                        '@id': 'https://araialldesign.com/#paolo',
                                        name: 'Paolo Tommasin',
                                        jobTitle: 'Footwear Design — Product Development',
                                        worksFor: { '@id': 'https://araialldesign.com/#organization' },
                                    },
                                ],
                                address: {
                                    '@type': 'PostalAddress',
                                    streetAddress: 'Via Chiesa 61',
                                    postalCode: '30039',
                                    addressLocality: 'Strà',
                                    addressRegion: 'Venice',
                                    addressCountry: 'IT',
                                },
                                contactPoint: {
                                    '@type': 'ContactPoint',
                                    contactType: 'customer service',
                                    email: 'info@alldesignsrl.com',
                                    availableLanguage: ['Italian', 'English'],
                                },
                                sameAs: [
                                    'https://www.instagram.com/all_design_srl',
                                    'https://it.linkedin.com/in/all-design-srl-footwear-design-e-product-development-8b0ba5199',
                                ],
                            },
                            {
                                '@type': 'WebSite',
                                '@id': 'https://araialldesign.com/#website',
                                url: 'https://araialldesign.com',
                                name: 'ARAIA ALL DESIGN',
                                inLanguage: [lang === 'en' ? 'en-US' : 'it-IT'],
                                publisher: { '@id': 'https://araialldesign.com/#organization' },
                            },
                        ],
                    })}
                </script>
            </Helmet>
            {/* iter97 · Single primary H1 for the page.
                Visually hidden via `.sr-only` so the two-world
                SplitScreen hero keeps its exact layout (previously
                every lens-clone produced an extra H1 ×6, Lighthouse
                flagged 12 H1 total). Screen readers still announce
                the brand headline; crawlers index it exactly once. */}
            <h1 className="sr-only">
                ARAIA ALL DESIGN — Two Vision, One Creative Direction
            </h1>
            <SplitScreen />
            {!showIntro && <AphorismChat />}
            {!showIntro && <HomeCTABlock />}
            {!showIntro && <Footer variant="dark" />}
            {showIntro && <IntroSequence onComplete={handleIntroComplete} />}
        </div>
    );
}
